import React, { useContext, useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Button, Box
} from '@mui/material';
import dayjs from 'dayjs';
import { DateRangeContext } from '../../../Context';
import axios from 'axios';
import { get_Call_Agentwisee } from '../../../constants';

const AgentCallCountTable = () => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December', 'Grand Total'
  ];

  const { filterType, start, end } = useContext(DateRangeContext);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [selectedFilter, setSelectedFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);

  // 🔥 Transform API response to component format
  const transformApiData = (apiResponse) => {
    if (!apiResponse.success || !apiResponse.data) {
      return [];
    }

    const { monthlyData, agents } = apiResponse.data;
    
    // Set agents for reference
    setAgents(agents || []);
    
    // 🔥 UPDATED: Always show agents even if no data
    if (!agents || agents.length === 0) {
      return []; // If no agents defined, return empty
    }

    // Transform data to match table structure where each row represents an agent
    const transformedData = agents.map(agentName => {
      const agentData = {
        agentName: agentName,
        date: dayjs().format('YYYY-MM-DD'),
        'Grand Total': 0
      };

      // Initialize all months with 0
      months.slice(0, -1).forEach(month => {
        agentData[month] = 0;
      });

      // Fill data from API response (only if monthlyData exists)
      if (monthlyData && monthlyData.length > 0) {
        monthlyData.forEach(monthData => {
          const monthName = monthData.month;
          if (agentData.hasOwnProperty(monthName)) {
            const callCount = monthData[agentName] || 0;
            agentData[monthName] = callCount;
            agentData['Grand Total'] += callCount;
          }
        });
      }

      return agentData;
    });

    // 🔥 UPDATED: Return all agents, even those with 0 totals
    return transformedData; // Remove the filter that hides agents with 0 data
  };

  // 🔥 Fetch data from API with filter parameters
  const fetchDataFromAPI = async (currentFilterType = null, customStart = null, customEnd = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const activeFilterType = currentFilterType || filterType;
      const activeStart = customStart || start;
      const activeEnd = customEnd || end;

      const params = {};
      
      switch (activeFilterType) {
        case 'today':
          params.filterType = 'today';
          break;
        case 'yesterday':
          params.filterType = 'yesterday';
          break;
        case 'last7':
          params.filterType = 'last7';
          break;
        case 'month':
          params.filterType = 'month';
          break;
        case 'custom':
          if (activeStart && activeEnd) {
            params.filterType = 'custom';
            params.customStartDate = dayjs(activeStart).format('YYYY-MM-DD');
            params.customEndDate = dayjs(activeEnd).format('YYYY-MM-DD');
          } else {
            params.filterType = 'none';
          }
          break;
        case 'none':
        default:
          params.filterType = 'none';
          break;
      }

      console.log('Fetching agent data with params:', params);

      // 🔥 API call to your agent-wise backend endpoint
      const response = await axios.get(`${get_Call_Agentwisee}`, {
        params: params,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Agent API Response:', response.data);

      // Transform and set data
      const transformed = transformApiData(response.data);
      setData(transformed);
      setFilteredData(transformed);
      
    } catch (error) {
      console.error('Error fetching agent data from API:', error);
      
      if (error.response) {
        setError(`Server Error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setError('Network Error: Cannot reach server. Make sure backend is running.');
      } else {
        setError(`Error: ${error.message}`);
      }
      
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataFromAPI();
  }, []);

  useEffect(() => {
    fetchDataFromAPI(filterType, start, end);
  }, [filterType, start, end]);

  // Calculate grand totals for filtered data
  const grandTotals = filteredData.reduce((acc, row) => {
    months.forEach(month => {
      acc[month] = (acc[month] || 0) + (row[month] ?? 0);
    });
    return acc;
  }, {});

  // Enhanced Excel download function
  const downloadExcel = () => {
    const timestamp = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const filterInfo = getFilterDescription();
    
    const header = ['Agent Name', ...months];
    
    const dataRows = filteredData.map(row => {
      const rowData = [row.agentName];
      months.forEach(month => {
        rowData.push(row[month] ?? 0);
      });
      return rowData;
    });

    const grandTotalRow = ['Grand Total'];
    months.forEach(month => {
      grandTotalRow.push(grandTotals[month] ?? 0);
    });

    const allRows = [
      ['Agent wise Call Count (Inbound/Outbound)'],
      [`Generated on: ${timestamp}`],
      [`Filter Applied: ${filterInfo}`],
      [`Total Agents: ${filteredData.length}`],
      [],
      header,
      ...dataRows,
      [],
      grandTotalRow,
      [],
      ['Agent Performance Summary:'],
      [`Top Performer: ${filteredData.length > 0 ? filteredData.reduce((prev, current) => (prev['Grand Total'] > current['Grand Total']) ? prev : current).agentName : 'N/A'}`],
      [`Total Active Agents: ${filteredData.filter(agent => agent['Grand Total'] > 0).length}`],
      [`Average Calls per Agent: ${filteredData.length > 0 ? Math.round(grandTotals['Grand Total'] / filteredData.length) : 0}`]
    ];

    let csvContent = 'data:text/csv;charset=utf-8,\uFEFF';
    csvContent += allRows.map(row => 
      Array.isArray(row) 
        ? row.map(cell => `"${cell}"`).join(',') 
        : `"${row}"`
    ).join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `agent_call_count_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFilterDescription = () => {
    if (filterType === 'today') return 'Today';
    if (filterType === 'yesterday') return 'Yesterday';
    if (filterType === 'last7') return 'Last 7 Days';
    if (filterType === 'month') return 'Current Month';
    if (filterType === 'custom') return `Custom Range: ${start} to ${end}`;
    return 'All Data (No Filter)';
  };

  const refreshData = () => {
    fetchDataFromAPI(filterType, start, end);
  };

  // 🔥 Get agent icon/emoji based on performance
  const getAgentIcon = (agentName, grandTotal) => {
    if (grandTotal > 2000) return ''; // Top performer
    if (grandTotal > 1500) return ''; // High performer
    if (grandTotal > 1000) return ''; // Good performer
    if (grandTotal > 500) return ''; // Average performer
    if (grandTotal > 0) return ''; // Active agent
    return ''; // Inactive agent
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
      <Box
        sx={{
          backgroundColor: '#097969',
          color: 'white',
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: '4px 4px 0 0',
          mb: 0
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            textAlign: 'left'
          }}
        >
          Agent wise Call Count (Inbound/Outbound)
          {loading && ' (Loading...)'}
          {error && ' (Error loading data)'}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={refreshData}
            disabled={loading}
            sx={{
              color: 'white',
              borderColor: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: 'white'
              }
            }}
          >
            🔄 Refresh
          </Button>

          <Button
            variant="contained"
            onClick={downloadExcel}
            disabled={filteredData.length === 0 || loading}
            sx={{
              backgroundColor: '#097969',
              fontWeight: 'bold',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: '#45a049',
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
                color: '#666'
              },
              transition: 'all 0.2s ease'
            }}
          >
            📥 Export 
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8ecf0ff' }}>
                Agent Name
              </TableCell>
              {months.map((month) => (
                <TableCell key={month} sx={{ fontWeight: 'bold', backgroundColor: '#e9eef3ff' }}>
                  {month === 'Grand Total' ? month : month.slice(0, 3)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={months.length + 1}
                  align="center"
                  sx={{
                    py: 3,
                    color: '#666'
                  }}
                >
                  Loading agent data...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell
                  colSpan={months.length + 1}
                  align="center"
                  sx={{
                    py: 3,
                    color: '#666'
                  }}
                >
                  Error: {error}
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={months.length + 1}
                  align="center"
                  sx={{
                    py: 3,
                    color: '#666'
                  }}
                >
                  No agents available
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filteredData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#fafafa'
                      },
                      '&:hover': {
                        backgroundColor: '#e3f2fd'
                      }
                    }}
                  >
                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                      {row.agentName}
                    </TableCell>
                    {months.map((month) => (
                      <TableCell 
                        key={month}
                        sx={{ borderBottom: '1px solid #eee' }}
                        align="center"
                      >
                        {/* 🔥 UPDATED: Always show 0 instead of — */}
                        <Box component="span" sx={{ 
                          display: 'inline-block',
                          minWidth: '20px' 
                        }}>
                          {row[month] ?? 0}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {/* Grand Total Row */}
                <TableRow
                  sx={{
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold'
                  }}
                >
                  <TableCell sx={{ borderTop: '2px solid #ddd' }}>
                    Grand Total
                  </TableCell>
                  {months.map(month => (
                    <TableCell key={month} sx={{ borderTop: '2px solid #ddd' }} align="center">
                      <Box component="span" sx={{ 
                        display: 'inline-block',
                        minWidth: '25px',
                        fontWeight: 'bold'
                      }}>
                        {grandTotals[month] ?? 0}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AgentCallCountTable;
